import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { startToolOperation } from '@saas-pages/shared/public/runtime'
import { useT } from '@/shared/i18n'
import { downloadBlob, exportFilename, exportPreviewImage } from '../model/imageExport'
import type {
  OpenImageViewerActionContext,
  OpenImageViewerConfig,
  OpenImageViewerExportFormat,
} from '../model/types'

interface ViewerExportButtonsProps {
  context: OpenImageViewerActionContext
  config: OpenImageViewerConfig
  formats: OpenImageViewerExportFormat[]
}

export function ViewerExportButtons({ context, config, formats }: ViewerExportButtonsProps) {
  const t = useT()
  const copy = t.viewer
  const [exporting, setExporting] = useState<OpenImageViewerExportFormat | null>(null)
  const [error, setError] = useState<string | null>(null)
  const generationRef = useRef(0)

  useEffect(() => () => {
    generationRef.current += 1
  }, [])

  const exportImage = useCallback(
    async (format: OpenImageViewerExportFormat) => {
      const generation = ++generationRef.current
      const analytics = config.analyticsContext ?? config.analytics
      const operation = analytics ? startToolOperation(analytics) : null
      const startTime = performance.now()
      setExporting(format)
      setError(null)

      try {
        const blob = await exportPreviewImage({
          previewUrl: context.image.previewUrl,
          width: context.image.width,
          height: context.image.height,
          format,
        })
        if (generation !== generationRef.current) return
        downloadBlob(blob, exportFilename(context.file.name, format))
        operation?.success(performance.now() - startTime)
      } catch {
        if (generation !== generationRef.current) return
        operation?.error('processing_failed')
        setError(copy.exportError)
      } finally {
        if (generation === generationRef.current) setExporting(null)
      }
    },
    [config.analytics, config.analyticsContext, context, copy.exportError],
  )

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2">
        {formats.map((format) => (
          <Button
            key={format}
            type="button"
            disabled={exporting !== null}
            onClick={() => void exportImage(format)}
          >
            {exporting === format
              ? copy.preparing
              : format === 'png'
                ? copy.downloadPng
                : copy.downloadJpg}
          </Button>
        ))}
      </div>
      {config.exportNote && (
        <p className="text-xs leading-5 text-muted-foreground">{config.exportNote}</p>
      )}
      {error && (
        <p className="text-sm leading-5 text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
